import { PickerField } from "@bryntum/core-thin";
import {
  CUSTOM_SUPPLY_YEAR_FILTER,
  CUSTOM_TIMELINE_YEAR_FILTER,
  DAYS_OF_WEEK,
  DEFAULT_START_OF_DAY,
  FULL_TIMELINE_YEAR_FILTER,
  HOURS_IN_DAY,
  MINUTES_IN_HOUR,
  PORTFOLIO_DATE_FILTER,
  SCHEDULER_FILTER_DATES,
  USER_TASKBOARD_DATE_FILTER,
} from "../../global/constants";
import {
  getFromLocalStorage,
  saveOnLocalStorage,
} from "../LocalStorageUtils/LocalStorageUtils";
import i18next from "i18next";
import { ta } from "date-fns/locale";
import { t } from "i18next";
import {
  applyWorkingDayEnd,
  workingDayEnd,
  workingDayStartHour,
} from "../schedulingCalendar";

export const getMonthFromNumber = (number) => {
  let maxMonth = 12,
    minMonth = 1,
    newNumber = 1;
  if (number > maxMonth) {
    newNumber = number - maxMonth;
  } else if (number < minMonth) {
    newNumber = maxMonth + number;
  } else {
    newNumber = number;
  }
  return newNumber;
};

export const formatDateRangePickerDate = (str) => {
  let formatedStr = str.replace(" ", "T");
  const hours = parseInt(formatedStr.substring(11, 13));

  if (formatedStr.includes("P")) {
    formatedStr = formatedStr.replace(
      /..:/,
      `${hours === 12 ? hours : (hours + 12) % 24}:`
    );
  } else if (formatedStr.includes("A") && hours === 12) {
    formatedStr = formatedStr.replace(/..:/, "00:");
  }

  formatedStr = formatedStr.replace(/ (A|P)M/, ":00");
  return new Date(formatedStr);
};

export function formatDate(date) {
  var d = new Date(date),
    month = "" + (d.getMonth() + 1),
    day = "" + d.getDate(),
    year = d.getFullYear();

  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;

  return [year, month, day].join("-");
}

export function getStartOfWeek(date) {
  return subtractDay(date, date.getDay());
}

export function getLastYear() {
  const start = new Date();
  start.setFullYear(start.getFullYear() - 1);
  start.setMonth(0);
  start.setDate(1);
  return {
    start: formatDate(start),
    finish: formatDate(new Date()),
  };
}

export function getNextYear() {
  const finish = new Date();
  finish.setFullYear(finish.getFullYear() + 2);
  finish.setMonth(0);
  finish.setDate(0);
  return {
    start: formatDate(new Date()),
    finish: formatDate(finish),
  };
}

export function addTwelveMonths(startDate) {
  const start = new Date(startDate);
  const newEnd = new Date(startDate);
  newEnd.setMonth(newEnd.getMonth() + 12);
  return {
    start: formatDate(start),
    finish: formatDate(newEnd),
  };
}

// The backend's audit/event timestamps (EventLog.createdAt, AuditLog.createdAt, etc.) are
// java.time.LocalDateTime, serialized WITHOUT a timezone marker (e.g. "2026-07-06T09:25:55")
// — the server JVM runs in GMT (see backend Dockerfile) but the string carries no 'Z'/offset.
// New Date("...no Z...") parses that string as the BROWSER's own local time, not as GMT — a
// French user then sees the raw GMT wall-clock number (e.g. "09:25") relabeled as if it were
// their own local time, 1-2h behind the real Paris time depending on DST. Append 'Z' so the
// string is parsed as the UTC instant it actually is (GMT has zero offset, so this is exact);
// the browser then renders it in the VIEWER's real local timezone, DST-aware, wherever they are.
export function parseServerLocalDateTimeAsUtc(dateTimeStr) {
  if (!dateTimeStr) return null;
  return new Date(dateTimeStr.endsWith("Z") ? dateTimeStr : `${dateTimeStr}Z`);
}

// YYYY-MM-DD of a server LocalDateTime as seen in the VIEWER's timezone. A raw
// `String(x).slice(0,10)` shows the GMT calendar date, which can be off by one day near
// midnight for a French viewer (e.g. a 23:30 Paris action stamped "…T22:30" GMT would slice
// to the wrong day only across midnight — and any evening action after 22:00/23:00 GMT rolls
// to the next day). Parse as UTC, then read the LOCAL Y/M/D so the displayed day matches the
// viewer's wall clock.
export function formatServerDateInViewerZone(dateTimeStr) {
  const d = parseServerLocalDateTimeAsUtc(dateTimeStr);
  if (!d || isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function removeTimezoneOffset(date) {
  // Preserve null/undefined — `new Date(null)` collapses to the 1970 epoch and
  // would silently corrupt optional date fields when persisted.
  if (date == null) return date;
  const _date = new Date(date);
  const timeZoneOffset = _date.getTimezoneOffset();
  return timeZoneOffset !== 0
    ? new Date(_date.setMinutes(_date.getMinutes() - timeZoneOffset))
    : _date;
}

export function getCurrentYear() {
  const start = new Date();
  const finish = new Date();
  start.setMonth(0);
  start.setDate(1);
  finish.setFullYear(finish.getFullYear() + 1);
  finish.setMonth(0);
  finish.setDate(0);
  return {
    start: formatDate(start),
    finish: formatDate(finish),
  };
}

export const getDefaultView = () => ({
  start: formatDate(removeMonthsFromCurrentDate(3)),
  finish: formatDate(addMonthsToCurrentDate(6)),
});

export const addMonthsToCurrentDate = (months, givenDate = new Date()) => {
  const date = new Date(givenDate);
  date.setMonth(date.getMonth() + months);
  return date;
};

export const removeMonthsFromCurrentDate = (months, givenDate = new Date()) => {
  const date = new Date(givenDate);
  date.setMonth(date.getMonth() - months);
  return date;
};

export const getEarliestStartDate = (array) =>
  array.reduce((earliestDate, { startDate }) => {
    const currentStartDate = new Date(startDate);
    return currentStartDate < earliestDate ? currentStartDate : earliestDate;
  }, new Date());

export const getLatestFinishDate = (array) =>
  array.reduce((latestDate, { finishDate }) => {
    const currentFinishDate = new Date(finishDate);
    return currentFinishDate > latestDate ? currentFinishDate : latestDate;
  }, new Date());

export function addDays(date, days) {
  let result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function removeDays(date, days) {
  let result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
}

export function addWeeksFromCurrentDate(weeks, givenDate = new Date()) {
  const date = new Date(givenDate);
  date.setDate(date.getDate() + weeks * 7);
  return date;
}

export function removeWeeksFromCurrentDate(weeks, givenDate = new Date()) {
  const date = new Date(givenDate);
  date.setDate(date.getDate() - weeks * 7);
  return date;
}

export function getFirstDateOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function getLastDateOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

export function getWeekNumber(date) {
  // Copy date so don't modify original
  var d = new Date(date);
  d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  // Set to nearest Thursday: current date + 4 - current day number
  // Make Sunday's day number 7
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  // Get first day of year
  var yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  // Calculate full weeks to nearest Thursday
  var weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  // Return array of year and week number
  return weekNo + 1;
}

// export function calculateEndDate(startDate, interval = 0) {
//   if (!+interval) return;
//
//   const isMonth = allowFeature(MONTH_ON_METRIC);
//   const endDate = new Date(startDate);
//
//   if (isMonth) {
//     endDate.setMonth(endDate.getMonth() + +interval);
//   } else {
//     endDate.setDate(endDate.getDate() + +interval * 7);
//   }
//
//   return endDate.toDateString();
// }

export const today = new Date();

const weekAccronyms = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const weekendAccronyms = ["Sat", "Sun"];

/**
 * The portfolio's DEFAULT calendar — the one PDM falls back to when scheduling
 * (CalendarService.findPortfolioDefaultCalendar backend-side). The Bryntum project
 * calendar must be this one, not an arbitrary `calendars[0]`: Bryntum derives task
 * durations from its project calendar, and PDM re-derives dates from that duration
 * against ITS calendar's daysOff — picking a different calendar FE-side makes the
 * two disagree (e.g. a 129-weekday duration overrunning a June-30 finish into July
 * once PDM skips 6 holidays). Backend serializes Calendar.isDefault() as `default`;
 * the other spellings are kept defensively.
 */
export function findDefaultCalendar(calendars) {
  if (!calendars || !calendars.length) return undefined;
  return (
    calendars.find(
      (cal) =>
        cal?.default === true ||
        cal?.default === 1 ||
        cal?.isDefault === true ||
        cal?.is_default === true ||
        cal?.is_default === 1
    ) || calendars[0]
  );
}

export function formatCalendars(calendars) {
  return calendars.map(
    ({ id, name, firstDate, lastDate, daysOfWeek, workDays }) => ({
      id,
      name,
      startDate: firstDate,
      endDate: lastDate,
      intervals: generateIntervalsFromCalendar(daysOfWeek, workDays),
      unspecifiedTimeIsWorking: false,
    })
  );
}

function generateIntervalsFromCalendar(daysOfWeek, workDays) {
  const intervals = [];
  daysOfWeek.forEach(addWorkingDayInterval, intervals);
  workDays.forEach(addDaysOfInterval, intervals);
  return intervals;
}

function addWorkingDayInterval(dayOfWeek) {
  const dayAcronym = dayOfWeek.slice(0, 3).toUpperCase();

  if (!dayAcronym.trim()) return null;

  const { hour, minute } = workingDayEnd();

  this.push({
    recurrentStartDate: `On ${dayAcronym} at ${workingDayStartHour()}:00`,
    recurrentEndDate: `On ${dayAcronym} at ${hour}:${String(minute).padStart(2, "0")}`,
    isWorking: true,
  });
}

export function formatCalendarsBasedOnShifts(calendars) {
  return calendars.map(
    ({ id, name, firstDate, lastDate, daysOfWeek, workDays, shifts }) => ({
      id,
      name,
      startDate: firstDate,
      endDate: lastDate,
      intervals: generateIntervalsFromCalendarBasedOnShifts(
        daysOfWeek,
        workDays,
        shifts
      ),
      unspecifiedTimeIsWorking: false,
    })
  );
}

function generateIntervalsFromCalendarBasedOnShifts(
  daysOfWeek,
  workDays,
  shifts
) {
  const intervals = [];
  daysOfWeek.forEach((day) =>
    addWorkingDayIntervalBasedOnShifts(day, shifts, intervals)
  );
  DAYS_OF_WEEK.forEach((day, index) => {
    if (!daysOfWeek.includes(day)) {
      const dayAcronym =
        "on " + day.substring(0, 1) + day.substring(1, 3).toLowerCase();
      const nextDay = DAYS_OF_WEEK[(index + 1) % DAYS_OF_WEEK.length];
      const nextDayAcronym =
        "on " + nextDay.substring(0, 1) + nextDay.substring(1, 3).toLowerCase();
      intervals.push({
        recurrentStartDate: dayAcronym,
        recurrentEndDate: nextDayAcronym,
        isWorking: false,
      });
    }
  });
  workDays.forEach(addDaysOfInterval, intervals);
  return intervals;
}

function addWorkingDayIntervalBasedOnShifts(dayOfWeek, shifts, intervals) {
  const dayAcronym = dayOfWeek.slice(0, 3).toUpperCase();
  if (!dayAcronym.trim()) return null;

  const mergedShifts = mergeShifts(shifts);
  createIntervalsBasedOnShifts(mergedShifts, dayAcronym, intervals);
}

function createIntervalsBasedOnShifts(mergedShifts, day, intervals) {
  return mergedShifts.forEach((interval) => {
    let endDay = day;
    if (interval.start > interval.end) {
      endDay = getNextDay(day);
    }

    intervals.push({
      recurrentStartDate: `On ${day} at ${interval.start.slice(0, -3)}`,
      recurrentEndDate: `On ${endDay} at ${interval.end.slice(0, -3)}`,
      isWorking: true,
    });
  });
}

function getNextDay(day) {
  const daysOrder = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
  const currentIndex = daysOrder.indexOf(day);

  return daysOrder[(currentIndex + 1) % 7];
}

function mergeShifts(shifts) {
  if (!shifts || !shifts.length) return [];

  let sortedShifts = shifts
    .slice()
    .sort((a, b) => a.startHour.localeCompare(b.startHour));

  let mergedIntervals = [
    { start: sortedShifts[0].startHour, end: sortedShifts[0].finishHour },
  ];

  for (let i = 1; i < sortedShifts.length; i++) {
    const currentShift = sortedShifts[i];
    const lastMergedInterval = mergedIntervals[mergedIntervals.length - 1];

    if (currentShift.startHour <= lastMergedInterval.end) {
      const isOverNightShift = currentShift.startHour > currentShift.finishHour;
      lastMergedInterval.end =
        lastMergedInterval.end < currentShift.finishHour || isOverNightShift
          ? currentShift.finishHour
          : lastMergedInterval.end;
    } else {
      mergedIntervals.push({
        start: currentShift.startHour,
        end: currentShift.finishHour,
      });
    }
  }

  return mergedIntervals;
}

export function dailyResourceWorkHoursBasedOnShifts(shifts) {
  const mergedShifts = mergeShifts(shifts);
  let totalHours = 0;

  mergedShifts.forEach((entry) => {
    const start = entry.start.split(":");
    const end = entry.end.split(":");

    let startTime = parseInt(start[0]) + parseInt(start[1]) / MINUTES_IN_HOUR;
    let endTime = parseInt(end[0]) + parseInt(end[1]) / MINUTES_IN_HOUR;

    if (endTime < startTime) {
      endTime += HOURS_IN_DAY;
    }

    totalHours += endTime - startTime;
  });

  return totalHours;
}

function addDaysOfInterval(dayOff) {
  if (!dayOff?.date) return;

  // Parse the day/month/year straight from the "YYYY-MM-DD" string and build
  // LOCAL-midnight dates. `new Date("YYYY-MM-DD")` parses as UTC, which shifts
  // the calendar day for viewers behind UTC; component construction is
  // timezone-proof so the interval always covers exactly the intended day.
  const [year, month, day] = String(dayOff.date).split("-").map(Number);
  if (!year || !month || !day) return;

  const startDate = new Date(year, month - 1, day);
  const endDate = addDay(startDate, 1);

  this.push({
    // Bryntum's interval date-field converter rejects a "YYYY/MM/DD" slash
    // string — it silently dropped startDate/endDate to undefined, so every
    // holiday/exception interval reached Bryntum WITHOUT a date and was ignored
    // (weekends greyed only via the recurring Mon-Fri working intervals).
    // Passing real Date objects bypasses string parsing entirely. The backend
    // serializes WorkDay.isWorkingDay() as `workingDay`; `isWorking` is kept as
    // a defensive fallback.
    startDate,
    endDate,
    isWorking: dayOff.workingDay ?? dayOff.isWorking ?? false,
  });
}

function addDay(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function subtractDay(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
}

function getDaysOffIntervals(daysOff) {
  const intervals = [];

  daysOff.forEach((dayOff) => {
    const dayOffAcronym = dayOff.slice(0, 3).toUpperCase();
    intervals.push({
      recurrentStartDate: `On ${dayOffAcronym} at 0:00`,
      recurrentEndDate: `On ${dayOffAcronym} at 23:59`,
      isWorking: false,
    });
  });

  return intervals;
}

function getDaysOff(fullWeek, workWeek) {
  return fullWeek.filter((day) => !workWeek.includes(day));
}

function getDateIndicator(days, baseAccronyms) {
  const accronyms = days.map(getAccronymOfDay);
  return baseAccronyms.filter((acc) => accronyms.includes(acc)).join(", ");
}

function getAccronymOfDay(day) {
  const firstLetter = day.substring(0, 1);
  const twoOtherLetters = day.substring(1, 3).toLowerCase();
  return `${firstLetter}${twoOtherLetters}`;
}

const workDaysByLocale = {
  "en-US": [
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
  ],
  "fr-FR": [
    "DIMANCHE",
    "LUNDI",
    "MARDI",
    "MERCREDI",
    "JEUDI",
    "VENDREDI",
    "SAMEDI",
  ],
  "es-ES": [
    "DOMINGO",
    "LUNES",
    "MARTES",
    "MIÉRCOLES",
    "JUEVES",
    "VIERNES",
    "SÁBADO",
  ],
  "de-DE": [
    "SONNTAG",
    "MONTAG",
    "DIENSTAG",
    "MITTWOCH",
    "DONNERSTAG",
    "FREITAG",
    "SAMSTAG",
  ],
  "it-IT": [
    "DOMENICA",
    "LUNEDÌ",
    "MARTEDÌ",
    "MERCOLEDÌ",
    "GIOVEDÌ",
    "VENERDÌ",
    "SABATO",
  ],
  "pt-PT": [
    "DOMINGO",
    "SEGUNDA-FEIRA",
    "TERÇA-FEIRA",
    "QUARTA-FEIRA",
    "QUINTA-FEIRA",
    "SEXTA-FEIRA",
    "SÁBADO",
  ],
  "ru-RU": [
    "ВОСКРЕСЕНЬЕ",
    "ПОНЕДЕЛЬНИК",
    "ВТОРНИК",
    "СРЕДА",
    "ЧЕТВЕРГ",
    "ПЯТНИЦА",
    "СУББОТА",
  ],
  "zh-CN": [
    "星期日",
    "星期一",
    "星期二",
    "星期三",
    "星期四",
    "星期五",
    "星期六",
  ],
  "ja-JP": [
    "日曜日",
    "月曜日",
    "火曜日",
    "水曜日",
    "木曜日",
    "金曜日",
    "土曜日",
  ],
  "ko-KR": [
    "일요일",
    "월요일",
    "화요일",
    "수요일",
    "목요일",
    "금요일",
    "토요일",
  ],
  "ar-SA": [
    "الأحد",
    "الاثنين",
    "الثلاثاء",
    "الأربعاء",
    "الخميس",
    "الجمعة",
    "السبت",
  ],
  "nl-NL": [
    "ZONDAG",
    "MAANDAG",
    "DINSDAG",
    "WOENSDAG",
    "DONDERDAG",
    "VRIJDAG",
    "ZATERDAG",
  ],
};

function getLocaleFromWorkDays(workDays) {
  for (const locale in workDaysByLocale) {
    const localeWorkDays = workDaysByLocale[locale];
    if (
      workDays.length === localeWorkDays.length &&
      workDays.every((day, index) => day === localeWorkDays[index])
    ) {
      return locale;
    }
  }

  // Default locale if no match is found
  return "en-US";
}

export function getNumberOfWorkingDays(
  startDate,
  finishDate,
  workDays,
  locale = null
) {
  const dayPeriod = 1000 * 3600 * 24;
  const start = new Date(startDate).getTime();
  const finish = new Date(finishDate).getTime();
  let currentDay = start;
  let counter = 0;

  if (!locale) {
    locale = getLocaleFromWorkDays(workDays);
  }

  while (currentDay < finish) {
    const currentDayName = new Date(currentDay)
      .toLocaleDateString(locale, { weekday: "long" })
      .toUpperCase();

    if (workDays.includes(currentDayName)) counter++;
    currentDay += dayPeriod;
  }

  return counter;
}

export const setHours = (date, hours) => {
  const result = new Date(date);

  result.setHours(hours);
  result.setMinutes(0);
  result.setSeconds(0);

  return result;
};

// start and finish are date, not json
export function guardAgainstStartAboveFinish(start, finish) {
  const object = { start, finish };
  if (start > finish) {
    const date = new Date(start);
    date.setDate(date.getDate() + 1);
    object.finish = date;
  }
  return object;
}

/**
 *
 * @returns return current date time as a string
 */
export function getCurrentDateTime() {
  let now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth() + 1;
  let date = now.getDate();
  let hours = now.getHours();
  let minutes = now.getMinutes();

  // Pad single digit numbers with a leading zero
  month = month < 10 ? "0" + month : month;
  date = date < 10 ? "0" + date : date;
  hours = hours < 10 ? "0" + hours : hours;
  minutes = minutes < 10 ? "0" + minutes : minutes;

  let formattedDateTime =
    year + "-" + month + "-" + date + "_" + hours + "-" + minutes;
  return formattedDateTime;
}

export const groupByYear = (data) => {
  return data.reduce((acc, item) => {
    const year = item.year;
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(item);
    return acc;
  }, {});
};

export const getLastDayOfYear = (dateOfReview) => {
  const date = new Date(dateOfReview);
  const year = date.getFullYear();
  const lastDay = new Date(year + 1, 0, 0);

  const yyyy = lastDay.getFullYear();
  const mm = String(lastDay.getMonth() + 1).padStart(2, "0");
  const dd = String(lastDay.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
};

export const datesAreEquals = (date1, date2) => {
  if (!date1 || !date2) return false;
  const date1JSON = new Date(date1).toJSON().substring(0, 10);
  const date2JSON = new Date(date2).toJSON().substring(0, 10);
  return date1JSON === date2JSON;
};

export const getStartOfYear = (shift = 0, date) => {
  const _date = date ? new Date(date) : new Date();
  return new Date(`${_date.getFullYear() + shift}-01-01`);
};

export const getEndOfYear = (shift = 0, date) => {
  const _date = date ? new Date(date) : new Date();
  return new Date(`${_date.getFullYear() + shift}-12-31`);
};

export const getStartOfMonth = (monthsToAdd = 0) => {
  const currentDate = new Date();
  return new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + monthsToAdd,
    1
  );
};

export const getOneDayBeforeNextYearForStartOfMonth = () => {
  const startDate = getStartOfMonth();
  startDate.setDate(startDate.getDate() - 1);
  startDate.setFullYear(startDate.getFullYear() + 1);
  return startDate;
};

export const getStartOfNextMonth = () => {
  const startDate = getStartOfMonth();
  startDate.setMonth(startDate.getMonth() + 1);

  return startDate;
};

export const addOneYear = () => {
  const startDate = getStartOfMonth();
  startDate.setFullYear(startDate.getFullYear() + 1);

  return startDate;
};

export const dateToIsoFormat = (date) => {
  const year = date.getFullYear();
  const month = getMonth(date);
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * The calendar day a value represents, as "YYYY-MM-DD", for any payload sent to the
 * backend (where these fields are java.time.LocalDate — a day, not an instant).
 *
 * A picker value (`<input type="date">`, Syncfusion, Bryntum) is a Date at LOCAL
 * midnight. `.toISOString()`/`.toJSON()` on it yields the UTC instant — 2026-08-03
 * local in Paris is "2026-08-02T22:00:00Z" — so slicing the first 10 characters reads
 * the UTC day and persists the PREVIOUS one for every user east of Greenwich. Reading
 * the local parts instead is exact in every timezone.
 *
 * A "YYYY-MM-DD" string is already the answer and is returned untouched: round-tripping
 * it through `new Date(str)` would parse it as UTC midnight and shift it the other way,
 * west of Greenwich.
 *
 * @param {Date|string|null|undefined} value
 * @returns {string|null} the local calendar day, or null when there is nothing to send
 */
export const toApiDate = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const parsed = new Date(trimmed);
    return isNaN(parsed.getTime()) ? null : dateToIsoFormat(parsed);
  }
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : dateToIsoFormat(date);
};

export const getMonth = (date) => {
  return (date.getMonth() + 1).toString().padStart(2, "0");
};

/**
 * Returns a new Date object representing a date that is one month after the provided date.
 *
 * @param {Date} date - The initial date.
 * @returns {Date} - A new Date object one month after the input date.
 */
export const addOneMonth = (date) => {
  const newDate = new Date(date);

  newDate.setMonth(date.getMonth() + 1);

  return newDate;
};

export const countMonthsBetweenDates = (start, finish) => {
  let months;
  const startDate = new Date(start);
  const finishDate = new Date(finish);

  months = (finishDate.getFullYear() - startDate.getFullYear()) * 12;
  months -= startDate.getMonth();
  months += finishDate.getMonth() + 1;
  return months <= 0 ? 0 : months;
};

export async function sleep(milliseconds) {
  return new Promise((resolve, reject) => {
    setTimeout(resolve, milliseconds);
  });
}

export const countDaysBetweenDates = (start, finish) => {
  const startTime = new Date(start).getTime();
  const finishTime = new Date(finish).getTime();

  return Math.floor((finishTime - startTime) / (1000 * 60 * 60 * 24));
};

export const setDayToStringDate = (date, day) => {
  const _date = new Date(date);
  return `${_date.getFullYear()}-${getMonth(_date)}-${day < 10 ? "0" : ""
    }${day}`;
};

const nextMonday = addDays(getStartOfWeek(addWeeksFromCurrentDate(1)), 1);
const nextSunday = removeDays(nextMonday, 1);
const formattedToday = formatDate(nextMonday);


export const getSchedulerFilterDatesOptions = (t) => [
  {
    id: "+1W",
    label: t("taskFocus.schedulerDateOptions.+1W"),
    default: true,
    start: formattedToday,
    finish: formatDate(addDays(getStartOfWeek(addWeeksFromCurrentDate(1)), 7)),
  },
  {
    id: "+2W",
    label: t("taskFocus.schedulerDateOptions.+2W"),
    start: formattedToday,
    finish: formatDate(addWeeksFromCurrentDate(2, nextSunday)),
  },
  {
    id: "+1M",
    label: t("taskFocus.schedulerDateOptions.+1M"),
    start: formattedToday,
    finish: formatDate(addMonthsToCurrentDate(1, nextSunday)),
  },
  {
    id: "+2M",
    label: t("taskFocus.schedulerDateOptions.+2M"),
    start: formattedToday,
    finish: formatDate(addMonthsToCurrentDate(2, nextSunday)),
  },
];

export function migratePrevousSchedulerDatesFilters() {
  const previousValue = getFromLocalStorage(SCHEDULER_FILTER_DATES, null);
  if (previousValue) {
    const filterIdsMigrator = {
      "-1W/+3W": "+2W",
      "+/-1M": "+1M",
      "+/-2M": "+2M",
      "+/-3M": "+2M",
      "+3M": "+2M",
    };
    if (previousValue.id in filterIdsMigrator) {
      const newFilterId = filterIdsMigrator[previousValue.id];
      const t = i18next.getFixedT(null, i18next.language);
      const newFilter = getSchedulerFilterDatesOptions(t).find(
        (filter) => filter.id === newFilterId
      );
      saveOnLocalStorage(SCHEDULER_FILTER_DATES, newFilter);
    }
  }
}
export const getDatePresets = (t) => {
  // Keep preset labels as translation keys rather than English literals. The
  // buttons call `t(label)`, so passing the key lets every supported locale
  // render the same date window without leaking the fallback language.
  const dateRangeLabel = (key, fallback) =>
    t(`global.dateRangePresets.${key}`, fallback);

  return ({
  [SCHEDULER_FILTER_DATES]: getSchedulerFilterDatesOptions(t),
  [USER_TASKBOARD_DATE_FILTER]: [
    {
      id: "1D",
      label: t("global.1D"),
      default: true,
      start: formatDate(new Date()),
      finish: formatDate(addDays(new Date(), 1)),
    },
    {
      id: "7D",
      label: t("global.7D"),
      start: formatDate(getStartOfWeek(new Date())),
      finish: formatDate(addDays(getStartOfWeek(new Date()), 7)),
    },
    {
      id: "14D",
      label: t("global.14D"),
      start: formatDate(getStartOfWeek(new Date())),
      finish: formatDate(addDays(getStartOfWeek(new Date()), 14)),
    },
  ],
  [PORTFOLIO_DATE_FILTER]: [
    {
      id: "Last Year",
      label: dateRangeLabel("lastYear", "Last Year"),
      ...getLastYear(),
    },
    {
      id: "Current Year",
      label: dateRangeLabel("currentYear", "Current Year"),
      ...getCurrentYear(),
    },
    {
      id: "Next Year",
      label: dateRangeLabel("nextYear", "Next Year"),
      ...getNextYear(),
    },
    {
      id: "+12 Months",
      label: dateRangeLabel("plus12Months", "+12 Months"),
      default: true,
      ...addTwelveMonths(new Date()),
    },
  ],
  [FULL_TIMELINE_YEAR_FILTER]: [
    {
      id: "Last Year",
      label: dateRangeLabel("lastYear", "Last Year"),
      start: formatDate(getStartOfYear(-1)),
      finish: formatDate(getEndOfYear(-1)),
    },
    {
      id: "Current Year",
      label: dateRangeLabel("currentYear", "Current Year"),
      start: formatDate(getStartOfYear(0)),
      finish: formatDate(getEndOfYear(0)),
    },
    {
      id: "Next Year",
      label: dateRangeLabel("nextYear", "Next Year"),
      start: formatDate(getStartOfYear(1)),
      finish: formatDate(getEndOfYear(1)),
    },
    {
      id: "+12 Months",
      label: dateRangeLabel("plus12Months", "+12 Months"),
      default: true,
      ...addTwelveMonths(new Date()),
    },
  ],
  [CUSTOM_TIMELINE_YEAR_FILTER]: [
    {
      id: "Last Year",
      label: dateRangeLabel("lastYear", "Last Year"),
      start: formatDate(getStartOfYear(-1)),
      finish: formatDate(getEndOfYear(-1)),
    },
    {
      id: "Current Year",
      label: dateRangeLabel("currentYear", "Current Year"),
      start: formatDate(getStartOfYear(0)),
      finish: formatDate(getEndOfYear(0)),
    },
    {
      id: "Next Year",
      label: dateRangeLabel("nextYear", "Next Year"),
      start: formatDate(getStartOfYear(1)),
      finish: formatDate(getEndOfYear(1)),
    },
    {
      id: "+6 months",
      label: dateRangeLabel("plus6Months", "+6 months"),
      start: formatDate(getStartOfMonth(0)),
      finish: formatDate(getStartOfMonth(6)),
    },
    {
      id: "+1 year",
      label: dateRangeLabel("plus1Year", "+1 year"),
      start: formatDate(getStartOfMonth(0)),
      finish: formatDate(getOneDayBeforeNextYearForStartOfMonth()),
    },
    {
      id: "+12 Months",
      label: dateRangeLabel("plus12Months", "+12 Months"),
      default: true,
      ...addTwelveMonths(new Date()),
    },
  ],
  [CUSTOM_SUPPLY_YEAR_FILTER]: [
    {
      id: "Last Year",
      label: dateRangeLabel("lastYear", "Last Year"),
      start: formatDate(getStartOfYear(-1)),
      finish: formatDate(getEndOfYear(-1)),
    },
    {
      id: "Current Year",
      label: dateRangeLabel("currentYear", "Current Year"),
      start: formatDate(getStartOfYear(0)),
      finish: formatDate(getEndOfYear(0)),
    },
    {
      id: "Next Year",
      label: dateRangeLabel("nextYear", "Next Year"),
      start: formatDate(getStartOfYear(1)),
      finish: formatDate(getEndOfYear(1)),
    },
    {
      id: "+6 months",
      label: dateRangeLabel("plus6Months", "+6 months"),
      start: formatDate(getStartOfMonth(0)),
      finish: formatDate(getStartOfMonth(6)),
    },
    {
      id: "+1 year",
      label: dateRangeLabel("plus1Year", "+1 year"),
      start: formatDate(getStartOfMonth(0)),
      finish: formatDate(getOneDayBeforeNextYearForStartOfMonth()),
    },
    {
      id: "+12 Months",
      label: dateRangeLabel("plus12Months", "+12 Months"),
      default: true,
      ...addTwelveMonths(new Date()),
    },
  ],
  });
};

export const getDateRangePreset = (id, t) => {
  // todo : remove this conditionnal for 1.48
  if (!t) {
    t = i18next.getFixedT(null, i18next.language);
  }
  if (id === SCHEDULER_FILTER_DATES) {
    if (localStorage.getItem("migrationOfSchedulerDates") !== "1") {
      migratePrevousSchedulerDatesFilters();
      localStorage.setItem("migrationOfSchedulerDates", "1");
    }
  }
  let item = JSON.parse(localStorage.getItem(id));
  const presets = getDatePresets(t)[id];

  if (!presets) return null;

  if (item?.id !== "custom") {
    const selectedPreset = presets.find((i) => i.id === item?.id);
    const defaultPreset = presets.find((p) => p.default) || presets[0];
    item = selectedPreset || defaultPreset;
  }

  return item;
};

export const getDateRange = (id) => {
  const preset = getDateRangePreset(id);
  if (!preset) return { start: null, finish: null };
  return { start: preset.start, finish: preset.finish };
};

/**
 * Overwrites the persisted range for {@code id} with its default preset (e.g. +1W) and returns
 * that default's {start, finish}. Used to force a view to always open on its default period
 * instead of restoring a previously-selected (possibly very wide) range.
 */
export const resetDateRangeToDefault = (id, t) => {
  if (!t) {
    t = i18next.getFixedT(null, i18next.language);
  }
  const presets = getDatePresets(t)[id];
  if (!presets || presets.length === 0) return { start: null, finish: null };
  const defaultPreset = presets.find((p) => p.default) || presets[0];
  saveDateRangePreset(id, defaultPreset);
  return { start: defaultPreset.start, finish: defaultPreset.finish };
};

/**
 * Validates a date interval object has valid start and finish dates.
 * @param {Object} interval - Object with start and finish properties
 * @returns {{ valid: boolean, error?: string }} - Validation result
 */
export const validateDateInterval = (interval) => {
  if (!interval) {
    return { valid: false, error: "Date interval is missing" };
  }

  const { start, finish } = interval;

  // Build list of missing fields for clearer error message
  const missingFields = [];
  if (!start) missingFields.push("start");
  if (!finish) missingFields.push("finish");
  
  if (missingFields.length > 0) {
    return {
      valid: false,
      error: `Missing required date fields: ${missingFields.join(" and ")}`,
    };
  }

  // Check if dates are valid date strings (YYYY-MM-DD format or ISO format)
  const startDate = new Date(start);
  const finishDate = new Date(finish);

  if (isNaN(startDate.getTime())) {
    return { valid: false, error: `Invalid start date format: ${start}` };
  }

  if (isNaN(finishDate.getTime())) {
    return { valid: false, error: `Invalid finish date format: ${finish}` };
  }

  if (startDate >= finishDate) {
    return { valid: false, error: "Start date must be before finish date" };
  }

  return { valid: true };
};

export const saveDateRangePreset = (id, preset) => {
  const item = JSON.stringify(preset);
  return localStorage.setItem(id, item);
};

export const getFullWeekOfDate = (someDate) => {
  const date = new Date(someDate);
  const monday = new Date(date.setDate(date.getDate() - date.getDay() + 1));
  const result = [new Date(monday)];
  while (monday.setDate(monday.getDate() + 1) && monday.getDay() !== 1) {
    result.push(new Date(monday));
  }
  return result;
};

export const getYearsBetweenDates = (firstDate, secondDate) => {
  const firstYear = +firstDate.slice(0, 4);
  const lastYear = +secondDate.slice(0, 4);
  const numberOfYears = lastYear - firstYear + 1;
  const years = new Array(numberOfYears);
  for (let i = 0; i < numberOfYears; i++) years[i] = (firstYear + i).toString();
  return years;
};

/**
 * function to match syncfusion time picker component with shifts
 */
export function formatHourAsDate(time) {
  const date = new Date();
  const [hour, minute, seconds] = time.split(":");
  date.setHours(+hour);
  date.setMinutes(+minute);
  date.setSeconds(+seconds);
  return date;
}

const convertNumberToStringWithTwoDigits = (num) =>
  num < 10 ? `0${num}` : `${num}`;

export function formatDateAsHour(date) {
  const hours = convertNumberToStringWithTwoDigits(date.getHours());
  const minutes = convertNumberToStringWithTwoDigits(date.getMinutes());
  const seconds = convertNumberToStringWithTwoDigits(date.getSeconds());
  return `${hours}:${minutes}:${seconds}`;
}

export function assessIfBaselineOfRecordChanged(updatedItem, record) {
  if (
    !updatedItem.timeMetrics.baselineEarlyStartDate ||
    !updatedItem.timeMetrics.baselineEarlyFinishDate ||
    !record.baselines.data[0]
  )
    return false;

  const startOfBaselineChanged = assessIfGivenElementOfBaselineChanged(
    updatedItem,
    record,
    "baselineEarlyStartDate",
    "startDate"
  );
  const finishOfBaselineChanged = assessIfGivenElementOfBaselineChanged(
    updatedItem,
    record,
    "baselineEarlyFinishDate",
    "endDate"
  );
  return startOfBaselineChanged || finishOfBaselineChanged;
}

function assessIfGivenElementOfBaselineChanged(
  updatedItem,
  record,
  timeMetricsField,
  baselineField
) {
  const baselineElement = new Date(record.baselines.data[0][baselineField]);
  const timeMetricsElement = updatedItem.timeMetrics[timeMetricsField];
  return timeMetricsElement !== baselineElement.toISOString().split("T")[0];
}

export function createDateToSetOnBaselinesForRecord(
  previousRecord,
  dateField,
  stringDate
) {
  return createDateWithHoursOfPreviousDateOfRecord(
    previousRecord,
    dateField,
    stringDate,
    true
  );
}

export function createDateToSetOnEarlyDatesForRecord(
  previousRecord,
  dateField,
  stringDate
) {
  return createDateWithHoursOfPreviousDateOfRecord(
    previousRecord,
    dateField,
    stringDate
  );
}

function createDateWithHoursOfPreviousDateOfRecord(
  previousRecord,
  dateField,
  stringDate,
  shouldHandleBaseline = false
) {
  const dateToSet = new Date(stringDate);
  if (shouldHandleBaseline) {
    const previousBaselineDate = previousRecord.baselines.getAt(0)[dateField];
    dateToSet.setHours(previousBaselineDate.getHours());
  } else {
    dateToSet.setHours(previousRecord[dateField].getHours());
  }
  return dateToSet;
}

// Calendar-day key computed in LOCAL time on both sides. A date-only ISO
// string ("2026-03-05") is parsed as LOCAL midnight (parseDateLocal) — the
// spec parses it as UTC midnight, which shifts the day for every timezone
// away from UTC when compared against a locally-parsed Date. That mixed
// parsing made the old toISOString()-based comparison read EVERY backend
// echo as "changed" on the first pass after load, mass-rewriting engine
// date fields across the whole planning (the 15s "first édition" freeze
// at 1708 tasks). Strings carrying a time component keep Date's own
// parsing (ISO local-time semantics).
// The one place that decides how a maybe-date-only value becomes a Date:
// date-only strings parse as LOCAL midnight (parseDateLocal — new Date(str)
// would read them as UTC midnight, the previous local day west of UTC);
// anything else keeps Date's own parsing. If the BE ever emits Z-suffixed
// timestamps, extend the detection HERE and every consumer follows.
export function parseMaybeDateOnly(value) {
  if (value instanceof Date) return value;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return parseDateLocal(value.trim());
  }
  return new Date(value);
}

function toLocalDayKey(value) {
  if (value == null) return null;
  const date = parseMaybeDateOnly(value);
  const time = date && date.getTime();
  if (time == null || Number.isNaN(time)) return null;
  return (
    date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate()
  );
}

export function datesAreDifferentBasedOnStringValue(date1, date2) {
  // Null/invalid inputs never throw (callers in the Gantt update path pass
  // `updatedItem.timeMetrics.*` which can be missing on some payloads).
  // Both-absent compares equal — an absent echo must not trigger a write.
  // NOTE: the BE serializes these dates date-only ("2026-03-05"); if it ever
  // switches to Z-suffixed timestamps, UTC-negative timezones would see the
  // UTC-anchored instant land on the previous local day — keep payloads
  // date-only or extend toLocalDayKey accordingly.
  const k1 = toLocalDayKey(date1);
  const k2 = toLocalDayKey(date2);
  if (k1 === null && k2 === null) return false;
  return k1 !== k2;
}

export function adjustDaysWithHoursOnActivity(activity) {
  activity.earlyStartDate = setHours(
    activity.earlyStartDate,
    DEFAULT_START_OF_DAY
  );
  activity.earlyEndDate = applyWorkingDayEnd(activity.earlyEndDate);
  activity.startDate = setHours(activity.startDate, DEFAULT_START_OF_DAY);
  activity.endDate = applyWorkingDayEnd(activity.endDate);
  activity.lateStartDate = setHours(
    activity.lateStartDate,
    DEFAULT_START_OF_DAY
  );
  activity.lateEndDate = applyWorkingDayEnd(activity.lateEndDate);
}

// PSM/PFM project milestones carry their own flags alongside `milestone`.
export const activityIsMilestone = (activity) =>
  Boolean(activity && (activity.milestone || activity.psm || activity.pfm));

// The one entry point callers should use: every activity needs its API date
// strings turned into Date objects, but a milestone must come out of that with
// start === end. Routing both cases through here keeps the two Gantts that do
// this pass from drifting apart again.
export function adjustActivityDates(activity) {
  if (activityIsMilestone(activity)) adjustMilestoneDatesOnActivity(activity);
  else adjustDaysWithHoursOnActivity(activity);
}

// A milestone is zero-duration, so it must not get the working-day end that
// adjustDaysWithHoursOnActivity stamps on a finish — but it still needs the
// string-to-Date conversion that pass performs. The Gantt's date fields reject
// the API's plain "YYYY-MM-DD" strings and store null, so a milestone left
// unconverted loses its position entirely and never renders.
export function adjustMilestoneDatesOnActivity(activity) {
  const atDayStart = (value) =>
    value == null ? value : setHours(parseMaybeDateOnly(value), DEFAULT_START_OF_DAY);

  activity.earlyStartDate = atDayStart(activity.earlyStartDate);
  activity.earlyEndDate = atDayStart(activity.earlyEndDate);
  activity.startDate = atDayStart(activity.startDate);
  activity.endDate = atDayStart(activity.endDate);
  activity.lateStartDate = atDayStart(activity.lateStartDate);
  activity.lateEndDate = atDayStart(activity.lateEndDate);
}

export function createCalendarPerResource(resources, calendars) {
  return resources
    .map((resource) => {
      const calendar = calendars.find(
        (cal) => cal.id === resource.selectedCalendarId
      );
      // No matching calendar, or a resource without shifts: DON'T build a per-resource
      // calendar. Building one anyway produced either a TypeError mid-mount (spread of
      // undefined -> daysOfWeek.forEach crash, which aborted the WHOLE calendar setup and
      // left the gantt on Bryntum's default calendar) or a 0-working-days calendar (no
      // shift intervals + unspecifiedTimeIsWorking:false). Returning null lets the
      // resource fall back to the project calendar (the portfolio one) - the intended
      // behavior for a resource with no shift pattern of its own.
      if (!calendar || !resource.shifts?.length) return null;
      return { ...calendar, id: resource.id, shifts: resource.shifts };
    })
    .filter(Boolean);
}

// Ids of the calendars to hand the Gantt engine for a scenario: every calendar
// referenced by ANY of the scenario's resources, plus the project default.
//
// Still far narrower than the whole portfolio (which can be hundreds — the
// engine re-evaluates every loaded calendar on each commit, the reason this
// filter exists at all), but deliberately NOT narrowed to the resources that
// happen to appear in the assignment records. Bryntum accounts for the assigned
// resources' calendars when it schedules a task (`TaskModel.ignoreResourceCalendar`,
// which formatOneTask sets to false), and it can only do so for calendars present
// in the project's calendar manager store. A calendar missing from that store
// silently leaves the task on the project calendar — which is how two tasks of
// equal duration whose resources work different days ended up rendering the same
// finish date.
export function computeScenarioCalendarIds(resources, defaultCalendarId) {
  const calendarIds = new Set(
    (resources || []).map((r) => r?.calendar).filter((id) => id != null)
  );
  if (defaultCalendarId != null) calendarIds.add(defaultCalendarId);
  return calendarIds;
}

// Ids of the calendars both scheduling engines of a scenario need.
//
// `resources` holds the GENERIC parents only, while `specificResources` holds
// the named ones — two disjoint lists, and a named resource carries its OWN
// selectedCalendarId, which can differ from its generic parent's. The Gantt
// schedules against named assignments and the Scheduler loads the specific
// resources outright, so both engines get the UNION rather than the generic
// half: a calendar missing from an engine's store silently leaves the task on
// the project calendar (see computeScenarioCalendarIds above).
//
// The union only ever grows the set, so no scheduling that works today can
// regress, and the per-commit cost stays bounded by the scenario — both lists
// belong to it.
export function computeGanttCalendarIds(
  resources,
  specificResources,
  defaultCalendarId
) {
  return new Set([
    ...computeScenarioCalendarIds(resources, defaultCalendarId),
    ...computeScenarioCalendarIds(specificResources, defaultCalendarId),
  ]);
}

export const isYearEqualOrAfterDOR = (dateOfReview, year) => {
  if (!dateOfReview || !year) return false;
  const yearDOR = +new Date(dateOfReview).getFullYear();
  const currentYear = +year;
  return yearDOR <= currentYear;
};

export function formatDateTime(dateTime) {
  const dateObject = new Date(dateTime);

  const year = dateObject.getFullYear();
  const month = ("0" + (dateObject.getMonth() + 1)).slice(-2); // Months are zero-based
  const day = ("0" + dateObject.getDate()).slice(-2);
  const hours = ("0" + dateObject.getHours()).slice(-2);
  const minutes = ("0" + dateObject.getMinutes()).slice(-2);

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

export function areDatesSame(dateTimeStr1, dateTimeStr2) {
  const date1 = new Date(dateTimeStr1).toDateString();
  const date2 = new Date(dateTimeStr2).toDateString();

  return date1 === date2;
}

export function calculateMidDate(activity) {
  const midTime =
    (activity.startDate.getTime() + activity.endDate.getTime()) / 2;
  return new Date(midTime);
}
export function getNextDayOfTheWeek(
  dayName,
  excludeToday = true,
  refDate = new Date()
) {
  const dayOfWeek = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"].indexOf(
    dayName.slice(0, 3).toLowerCase()
  );
  if (dayOfWeek < 0) return;

  const resultDate = new Date(refDate);
  resultDate.setHours(0, 0, 0, 0);
  resultDate.setDate(
    resultDate.getDate() +
    +!!excludeToday +
    ((dayOfWeek + 7 - resultDate.getDay() - +!!excludeToday) % 7)
  );
  return resultDate;
}

export function getNextDayOfWeek(date, dayOfWeek) {
  const resultDate = new Date(date);
  resultDate.setDate(
    date.getDate() + ((7 + dayOfWeek - date.getDay()) % 7 || 7)
  );
  return resultDate;
}

export function getNextMonday(date = new Date()) {
  const d = new Date(date);
  d.setDate(d.getDate() + ((1 + 7 - d.getDay()) % 7 || 7));
  return d;
}

export function getNextSunday(date = new Date()) {
  const d = new Date(date);
  d.setDate(d.getDate() + ((0 + 7 - d.getDay()) % 7 || 7));
  return d;
}

export function assessIfTaskRecordIsLate(record) {
  if (!record.deadlineDate || !record.endDate) return false;

  return record.deadlineDate.getTime() < record.endDate.getTime();
}

export const TODAY_BUTTON = {
    get items() {
    return {
    button: {
      type: "button",
      text: i18next.t("global.today"),
      onClick({ source }) {
        const today = new Date();
        source.parent.value = today;
        },
      },
    };
  },
};

export const DATE_COLUMN_EDITOR_WITH_TODAY_BUTTON = {
  get picker() {
    return {
    bbar: {
      items: {
        button: {
          type: "button",
          text: i18next.t("global.today"),
          onClick({ source }) {
            const today = new Date();
            source.parent.parent.value = today;
          },
          },
        },
      },
    };
  },
};

export function getLatestDate(firstDate, secondDate) {
  if (typeof firstDate === "string") firstDate = new Date(firstDate);
  if (typeof secondDate === "string") secondDate = new Date(secondDate);
  guardAgainstInvalidDate(firstDate, secondDate);
  return firstDate.getTime() > secondDate.getTime() ? firstDate : secondDate;
}

/**
 * Parses a "YYYY-MM-DD" date string as local midnight, avoiding UTC-shift issues.
 *
 * Using `new Date("YYYY-MM-DD")` interprets the value as UTC midnight, which
 * can display as the previous local calendar day for users in UTC− timezones
 * (negative offsets). This helper always constructs the Date in local time so
 * the displayed date matches the backend value exactly.
 *
 * @param {string|null|undefined} dateStr - ISO date string (e.g. "2026-12-31")
 * @returns {Date|null} Local-midnight Date, or null when dateStr is falsy
 */
export function parseDateLocal(dateStr) {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0);
}

function guardAgainstInvalidDate(...dates) {
  for (const date of dates)
    if (!(date instanceof Date))
      throw new Error("Invalid input.");
}
